import { useApi } from "../hooks/useApi";
import { useState } from "react";

const yen = (n) => Number(n || 0).toLocaleString("ja-JP") + "円";

function load(key) {
  try {
    const v = JSON.parse(localStorage.getItem(key));
    if (Array.isArray(v)) return v;
  } catch { /* 未登録なら空 */ }
  return [];
}

// 帳簿の支出構成から「使えるかもしれない補助金の方向性」を提案するルール
// （税務判断ではなく、補助金タブでの確認を促す入口）
const HINT_RULES = [
  { cats: ["燃料費", "水道光熱費"], threshold: 0.2, hint: "燃料・光熱費の割合が大きめです。施設園芸の燃料高騰対策（セーフティネット）や省エネ設備の補助が使えるかもしれません。", icon: "⛽" },
  { cats: ["農薬・肥料"], threshold: 0.2, hint: "農薬・肥料のコストが大きめです。化学肥料・農薬を減らす取組には「環境保全型農業直接支払交付金」など面積あたりの交付金があります。", icon: "🌿" },
  { cats: ["修繕費"], threshold: 0.15, hint: "修繕費がかさんでいます。機械・施設の更新は補助金の対象になることが多いです。※契約・購入の前に申請が原則です。", icon: "🔧" },
  { cats: ["雇用労務費"], threshold: 0.2, hint: "人件費の割合が大きめです。雇用関係の助成や、省力化機械・スマート農業の導入補助を検討する価値があります。", icon: "👥" },
  { cats: ["農業機械"], threshold: 0.15, hint: "機械への支出があります。次の買い替えは、発注前に補助金の診断をお試しください（発注後では対象外になります）。", icon: "🚜" },
];

export default function KeieiScreen({ onGoSubsidy }) {
  const entries = load("nouen_ledger_entries");
  const fields = load("nouen_fields");
  const [aiPlan, setAiPlan] = useState("");
  const { call, loading } = useApi();

  const income = entries.filter((e) => e.type === "income").reduce((s, e) => s + Number(e.amount), 0);
  const expense = entries.filter((e) => e.type === "expense").reduce((s, e) => s + Number(e.amount), 0);
  const profit = income - expense;
  const totalArea = fields.reduce((s, f) => s + Number(f.area || 0), 0);

  // カテゴリ別支出
  const byCat = {};
  entries.filter((e) => e.type === "expense").forEach((e) => {
    byCat[e.cat || "その他"] = (byCat[e.cat || "その他"] || 0) + Number(e.amount);
  });
  const catList = Object.entries(byCat).sort((a, b) => b[1] - a[1]);

  // 補助金ヒント（支出構成から）
  const hints = expense > 0
    ? HINT_RULES.filter((r) => r.cats.reduce((s, c) => s + (byCat[c] || 0), 0) / expense >= r.threshold)
    : [];
  if (profit < 0 && entries.length > 0) {
    hints.push({ icon: "🛡️", hint: "収支がマイナスです。収入保険（青色申告が要件）や、無料の経営相談窓口（農業経営・就農支援センター）の活用を検討しましょう。" });
  }

  const getAiPlan = async () => {
    const text = await call({
      maxTokens: 900,
      system: "あなたは農業経営に詳しいアドバイザーです。税務の個別判断（税額計算・申告の可否）はせず、必要なら税理士・行政書士への相談を促してください。断定的な補助金採択の保証はしないでください。",
      messages: [{
        role: "user",
        content: `農家の経営データです。
収入 ${yen(income)}／支出 ${yen(expense)}／収支 ${yen(profit)}
支出内訳: ${catList.map(([c, v]) => `${c} ${yen(v)}`).join("、") || "なし"}
圃場: ${fields.length ? fields.map((f) => `${f.name}(${f.area}a・${f.crop})`).join("、") : "未登録"}（合計${totalArea}a）

この経営状況を見て、①経営のよい点、②改善の打ち手（コスト・売上の両面）、③検討すべき補助金の方向性、を農家さんに話しかける平易な言葉で各2文程度、全体400字以内でまとめてください。`,
      }],
    });
    setAiPlan(text);
  };

  return (
    <div className="screen">
      <h2 className="screen-title">📊 経営のようす</h2>

      {entries.length === 0 && fields.length === 0 ? (
        <p className="ai-tip-text" style={{ textAlign: "center", padding: "16px 0" }}>
          「帳簿」に収支を、「圃場」に農地を登録すると、ここに経営のまとめと補助金のヒントが表示されます。
        </p>
      ) : (
        <>
          {/* 収支サマリー */}
          <div className="summary-grid">
            <div className="summary-card" style={{ background: "#e8f5e9" }}>
              <div className="summary-label">💰 収入</div>
              <div className="summary-val" style={{ color: "#2e7d32" }}>{yen(income)}</div>
            </div>
            <div className="summary-card" style={{ background: "#fce4ec" }}>
              <div className="summary-label">💸 支出</div>
              <div className="summary-val" style={{ color: "#c62828" }}>{yen(expense)}</div>
            </div>
            <div className="summary-card" style={{ background: profit >= 0 ? "#e3f2fd" : "#fff3e0" }}>
              <div className="summary-label">📊 収支</div>
              <div className="summary-val" style={{ color: profit >= 0 ? "#1565c0" : "#e65100" }}>{yen(profit)}</div>
            </div>
            <div className="summary-card" style={{ background: "#f1f8e9" }}>
              <div className="summary-label">📐 農地合計</div>
              <div className="summary-val" style={{ color: "#33691e" }}>{totalArea}a</div>
            </div>
          </div>

          {/* 支出の内訳（バー表示） */}
          {catList.length > 0 && (
            <div style={{ background: "#fafafa", border: "1px solid #eee", borderRadius: 10, padding: "0.75rem", margin: "0.75rem 0" }}>
              <div style={{ fontWeight: 700, fontSize: "0.85rem", marginBottom: "0.5rem" }}>お金の使いみち（多い順）</div>
              {catList.map(([c, v]) => (
                <div key={c} style={{ marginBottom: "0.4rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "#555" }}>
                    <span>{c}</span><span>{yen(v)}（{Math.round(v / expense * 100)}%）</span>
                  </div>
                  <div style={{ background: "#e0e0e0", borderRadius: 4, height: 8 }}>
                    <div style={{ background: "#2d6a4f", width: `${Math.round(v / expense * 100)}%`, height: 8, borderRadius: 4 }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 帳簿×補助金の連動ヒント */}
          {hints.length > 0 && (
            <div style={{ background: "#fff8e1", border: "1px solid #f5a623", borderRadius: 10, padding: "0.75rem", marginBottom: "0.75rem" }}>
              <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#8a5a00", marginBottom: "0.4rem" }}>💡 帳簿から見つかった補助金のヒント</div>
              {hints.map((h, i) => (
                <p key={i} style={{ fontSize: "0.78rem", color: "#555", margin: "0 0 0.4rem" }}>{h.icon} {h.hint}</p>
              ))}
              {onGoSubsidy && (
                <button className="primary-btn" style={{ marginTop: "0.2rem" }} onClick={onGoSubsidy}>
                  📋 補助金をさがす
                </button>
              )}
            </div>
          )}

          {/* AI経営プラン */}
          <button className="ai-tip-btn" onClick={getAiPlan} disabled={loading}>
            {loading ? "分析中..." : "🤖 AIに経営のまとめを聞く"}
          </button>
          {aiPlan && (
            <div className="ai-tip-box">
              <div className="ai-tip-header">🌾 農援AIの経営メモ</div>
              <p className="ai-tip-text" style={{ whiteSpace: "pre-wrap" }}>{aiPlan}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
